/**
 * The push channel, in a browser.
 *
 * `EventSource` rather than the shared [KtorServerEventTransport], and the reason is not
 * preference. **`EventSource` cannot set headers** — which would be fatal for a bearer-carrying
 * client and is exactly right here, because the browser's session is an `HttpOnly` cookie it
 * attaches by itself and which page script deliberately cannot read. The one carrier that
 * cannot be given a header is the one carrier that does not need one.
 *
 * It also reconnects natively, which is why [stream] does nothing more than translate one
 * connection's lifetime into a suspend function that returns when it ends. The retry policy
 * stays where every other client's retry policy is — in [ServerEventStream] — so that "a
 * reconnect means re-read" is one rule with one implementation rather than a browser-shaped
 * exception to it.
 *
 * ── Why native reconnection is turned off rather than used ───────────────────
 *
 * `EventSource` reconnects on its own after `retry:` milliseconds, and if this file simply let
 * it, nothing in the shared model would ever learn that a reconnection had happened — so the
 * re-read that a reconnection *means* would never run. That is precisely the failure LPN-27's
 * "re-read on every successful connect, not only the first" exists to prevent, and it would be
 * invisible: the browser would be connected, listening, and quietly holding whatever it had
 * when the connection dropped.
 *
 * So `onerror` closes the source and returns, handing the decision back to the shared loop.
 * The cost is that the browser's own backoff is unused; the gain is one reconnect policy across
 * three platforms and one place where "and now go and read" is written.
 */
package se.soderbjorn.lunapin.web

import kotlin.coroutines.resume
import kotlinx.coroutines.suspendCancellableCoroutine
import org.w3c.dom.EventSource
import se.soderbjorn.lunapin.client.ServerEventTransport
import se.soderbjorn.lunapin.clientserver.ApiRoutes
import se.soderbjorn.lunapin.clientserver.ServerEvent
import se.soderbjorn.lunapin.clientserver.ServerEventCodec

/**
 * One `EventSource` per connection attempt.
 *
 * No base URL: the server that served this bundle is the server it talks to, which is the whole
 * of the browser's deployment story and the reason there is no CORS anywhere in this project.
 */
class BrowserEventSourceTransport : ServerEventTransport {

    override suspend fun stream(onConnected: () -> Unit, onEvent: (ServerEvent) -> Unit) {
        suspendCancellableCoroutine { continuation ->
            val source = EventSource(ApiRoutes.EVENTS)

            // Guarded, because `onerror` can fire after `onopen` on the same source and both
            // paths resume: a second resume is an IllegalStateException thrown from inside a
            // DOM callback, whose stack trace points here rather than at the cause.
            fun finish() {
                source.close()
                if (continuation.isActive) continuation.resume(Unit)
            }

            source.onopen = { onConnected() }
            source.onmessage = { message ->
                // A comment frame — the heartbeat — never reaches here: `EventSource` swallows
                // it, which is the whole point of a comment. What arrives is a `data:` payload,
                // and one this client cannot read is skipped rather than fatal. See
                // `ServerEventCodec`, where that forward-compatibility argument is made.
                (message.data as? String)?.let { ServerEventCodec.decode(it)?.let(onEvent) }
            }
            // Fires for a connection that failed to open *and* for one that dropped, with no
            // way to tell them apart — which is fine, because the shared loop treats both the
            // same way and always has.
            source.onerror = { finish() }

            continuation.invokeOnCancellation { source.close() }
        }
    }
}
